import { useNavigate } from "react-router-dom";
import { Minus, Plus, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
    SheetFooter,
} from "@/components/ui/sheet";
import { useCart } from "@/store/useCart";
import { formatPrice } from "@/lib/utils";
import { useState } from "react";

export function CartDrawer() {
    const { items, removeItem, updateQuantity, getTotalPrice, getTotalItems } = useCart();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const handleCheckout = () => {
        setOpen(false);
        navigate("/checkout");
    };

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="relative">
                    <ShoppingCart className="h-5 w-5" />
                    {getTotalItems() > 0 && (
                        <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground font-bold leading-none">
                            {getTotalItems()}
                        </span>
                    )}
                </Button>
            </SheetTrigger>
            <SheetContent className="flex w-full flex-col pr-0 sm:max-w-lg">
                <SheetHeader className="px-1">
                    <SheetTitle>Shopping Cart ({getTotalItems()})</SheetTitle>
                </SheetHeader>
                <div className="flex-1 overflow-y-auto py-4 pr-6">
                    {items.length === 0 ? (
                        <div className="flex h-full flex-col items-center justify-center space-y-2 text-center">
                            <ShoppingCart className="h-12 w-12 text-muted-foreground" />
                            <span className="text-lg font-medium text-muted-foreground">
                                Your cart is empty
                            </span>
                            <Button variant="link" onClick={() => setOpen(false)}>Continue Shopping</Button>
                        </div>
                    ) : (
                        <ul className="grid gap-4">
                            {items.map((item) => (
                                <li key={`${item.id}-${item.variantId || ''}`} className="flex items-start gap-4">
                                    <div className="relative h-20 w-20 overflow-hidden rounded-md border bg-muted">
                                        {item.image ? (
                                            <img
                                                src={item.image}
                                                alt={item.name}
                                                className="h-full w-full object-cover"
                                            />
                                        ) : (
                                            <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                                                No Img
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex flex-1 flex-col justify-between self-stretch">
                                        <div className="grid gap-1">
                                            <h3 className="line-clamp-2 font-medium">
                                                {item.name}
                                            </h3>
                                            {/* Variant info if exists */}
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-1">
                                                <Button variant="outline" size="icon" className="h-6 w-6" onClick={() => updateQuantity(item.id, item.quantity - 1)}>
                                                    <Minus className="h-3 w-3" />
                                                </Button>
                                                <span className="w-8 text-center text-sm">{item.quantity}</span>
                                                <Button variant="outline" size="icon" className="h-6 w-6" onClick={() => updateQuantity(item.id, item.quantity + 1)}>
                                                    <Plus className="h-3 w-3" />
                                                </Button>
                                            </div>
                                            <div className="flex flex-col items-end">
                                                <span className="font-semibold">
                                                    {formatPrice(item.price * item.quantity)}
                                                </span>
                                                <button
                                                    onClick={() => removeItem(item.id)}
                                                    className="text-xs text-muted-foreground hover:text-destructive hover:underline"
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                {items.length > 0 && (
                    <div className="pr-6">
                        <div className="flex items-center justify-between border-t py-4 font-semibold">
                            <span>Subtotal</span>
                            <span>{formatPrice(getTotalPrice())}</span>
                        </div>
                        <div className="text-xs text-muted-foreground text-center mb-4">
                            Shipping calculated at checkout.
                        </div>
                        <SheetFooter>
                            <Button className="w-full" onClick={handleCheckout}>Checkout</Button>
                        </SheetFooter>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}
